import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch } from 'react-router';
import { Layout } from './components/Layout';
import Home from './pages/Home';
//import AuthorizeRoute from './components/api-authorization/AuthorizeRoute';
import ApiAuthorizationRoutes from './components/api-authorization/ApiAuthorizationRoutes';
import { ApplicationPaths } from './components/api-authorization/ApiAuthorizationConstants';

import 'devextreme/dist/css/dx.light.css';
import './custom.css'

import { useFetchData, useListSourceOptions } from './components/hooks/DataSource';
import useDutchDevExtreme from './components/hooks/DevExtremeLocale';
import ScrollToTop from './components/ScrollToTop';
import PageWithDataGridEditor, { IPageWithDataGridEditorProps } from './pages/beheer/PageWithDataGridEditor';
import GoedePraktijk from './pages/beheer/GoedePraktijk';
import VisitatieRapport from './pages/beheer/VisitatieRapport';
import OperationeleDoelstellingen from './pages/beheer/OperationeleDoelstellingen';
import ZoekRapporten from './pages/ZoekRapporten';
import ZoekGoedePraktijk from './pages/ZoekGoedePraktijk';
import ZoekShmPrestaties from './pages/ZoekShmPrestaties';
import AuthorizationRoute from './components/api-authorization/AuthorizationRoute';
import { IApiUrls } from './models/IApiUrls';
import { IListsModel } from './models/IHomeModel';
import useAppRoutes from './AppRoutes';
import Gebruikers from './pages/beheer/Gebruikers';
import { LoadPanel } from 'devextreme-react';
import { IDocumentPrivate } from './models/IDocument';

export interface IPageLoading {
    setPageLoading: (isLoading: boolean) => void;
}

const App = () => {

    useDutchDevExtreme()

    const routePaths = useAppRoutes();
    const apiUrls = useMemo((): IApiUrls => ({
        home:                       process.env.PUBLIC_URL + "/api/home",
        gemeenten:                  process.env.PUBLIC_URL + "/api/gemeente",
        themas:                     process.env.PUBLIC_URL + "/api/thema",
        shms:                       process.env.PUBLIC_URL + "/api/shm",
        operationeleDoelstellingen: process.env.PUBLIC_URL + "/api/operationeledoelstelling",
        visitatieRapporten:         process.env.PUBLIC_URL + "/api/visitatierapport",
        goedePraktijken:            process.env.PUBLIC_URL + "/api/goedepraktijk",
        documenten:                 process.env.PUBLIC_URL + "/api/document",
        gebruikers:                 process.env.PUBLIC_URL + '/api/users',

        zoekPrestaties:             process.env.PUBLIC_URL + '/api/zoek/prestaties',
        zoekRapportGemeente:        process.env.PUBLIC_URL + '/api/zoek/rapport/gemeente',
        zoekRapportShm:             process.env.PUBLIC_URL + '/api/zoek/rapport/shm',
        zoekGoedePraktijk:          process.env.PUBLIC_URL + '/api/zoek/goede-praktijk',

    }), [])

    const [lists, setLists] = useState<IListsModel>({});

    const fetch = useFetchData();

    const refreshLists = useCallback(() => {
        setIsLoading(prev => ({ ...prev, homeData: true }));
        fetch(apiUrls.home).then(data => {
            setLists(data);
            setIsLoading(prev => ({ ...prev, homeData: false }));
        }).catch(() => setIsLoading(prev => ({ ...prev, homeData: false })));
    }, [fetch, apiUrls])

    useEffect(refreshLists, [refreshLists])

    const gemSource = useListSourceOptions(lists.gemeenten, "naam");

    const pages = useMemo((): {
        secure?: boolean,                           // use AuthorizeRoute?
        path: string,                               //react route path
        component: (props: any) => JSX.Element,     //Component used with this route.
        props?: IPageWithDataGridEditorProps
    }[] => ([

        // visitatierapporten
        {
            secure: true,
            path: routePaths.visitatieRapporten,
            component: PageWithDataGridEditor,
            props: {
                apiUrl: apiUrls.visitatieRapporten,
                pageTitle: "Visitatierapporten",
                editPath: routePaths.visitatieRapportenEdit,
                firstColumn: "naam",
                stateStoreKey: "visitatierapportenlist",
                columns: [{ dataField: "shm.naam" }, { dataField: "gepubliceerd", width: "auto" }]
            }
        },
        {
            secure: true,
            path: routePaths.visitatieRapportenEdit,
            component: VisitatieRapport,
            props: { apiUrl: apiUrls.visitatieRapporten }
        },

        // Goede praktijken
        {
            secure: true,
            path: routePaths.goedePraktijken,
            component: PageWithDataGridEditor,
            props: {
                apiUrl: apiUrls.goedePraktijken,
                pageTitle: "Goede praktijken",
                editPath: routePaths.goedePraktijkenEdit,
                firstColumn: "titel",
                stateStoreKey: "goedpraktijkenlist",
                columns: [{ dataField: "gepubliceerd", width: "auto" }]
            }
        },
        {
            secure: true,
            path: routePaths.goedePraktijkenEdit,
            component: GoedePraktijk,
            props: { apiUrl: apiUrls.goedePraktijken }
        },

        // shms
        {
            secure: true,
            path: routePaths.shms,
            component: PageWithDataGridEditor,
            props: {
                apiUrl: apiUrls.shms,
                pageTitle: "Sociale huisvestingmaatschappijen",
                onSaved: refreshLists,
                stateStoreKey: "shmlist",
                columns: [
                    { dataField: "naam", validationRules: [{ type: "required" }] },
                    { dataField: "nummer", validationRules: [{ type: "required" } ] },
                    {
                        dataField: "maatschappelijkeZetelId",
                        validationRules: [{ type: "required" }],
                        caption: "Maatschappelijke zetel",
                        lookup: {
                            dataSource: gemSource,                            
                            displayExpr: "naam",
                            valueExpr: "id"
                        }
                    },
                ]
            }
        },

        // gemeenten
        {
            secure: true,
            path: routePaths.gemeenten,
            component: PageWithDataGridEditor,
            props: {
                apiUrl: apiUrls.gemeenten,
                pageTitle: "Gemeenten",
                onSaved: refreshLists,
                stateStoreKey: "gemeentelist",
                columns: [
                    { dataField: "naam", validationRules: [{ type: "required" }] },
                    { dataField: "postcode", dataType: "number", validationRules: [{ type: "required" }] },
                    { dataField: "niscode", dataType: "number", validationRules: [{ type: "required" }] }]
            }
        },

        // themas
        {
            secure: true,
            path: routePaths.themas,
            component: PageWithDataGridEditor,
            props: {
                apiUrl: apiUrls.themas,
                pageTitle: "Thema's",
                onSaved: refreshLists,
                stateStoreKey: "themalist",
                columns: [
                    { dataField: "naam", defaultSortIndex: 0, defaultSortOrder: "asc", validationRules: [{ type: "required" }] }
                ]
            }
        },

        // Documenten
        {
            secure: true,
            path: routePaths.documenten,
            component: PageWithDataGridEditor,
            props: {
                apiUrl: apiUrls.documenten,
                pageTitle: "Documenten",
                stateStoreKey: "documentenlist",
                allowAdd: false,
                columns: [
                    { dataField: "fileName", defaultSortIndex: 0, defaultSortOrder: "asc", validationRules: [{ type: "required" }] },
                    { dataField: "countUsed", caption: "# Gebruikt", allowEditing: false, width: "auto" },
                    {
                        type: "buttons", width: 75, buttons: [
                            { name: "delete", visible: (e: { row: { isNewRow: boolean, data: IDocumentPrivate } }) => !e.row.data.countUsed }
                        ]
                    }
                ]
            }
        },

        // operationele doelstellingen
        {
            secure: true,
            path: routePaths.operationeleDoelstellingen,
            component: OperationeleDoelstellingen,
            props: {
                apiUrl: apiUrls.operationeleDoelstellingen,
                pageTitle: "Operationele doelstellingen",                
                onSaved: refreshLists,
                stateStoreKey: "operationeledoelstellinglist",
            }
        },
        // gebruikers
        {
            secure: true,
            path: routePaths.gebruikers,
            component: Gebruikers,            
        },

        // Zoek pagina's
        {
            path: routePaths.zoekRapporten,
            component: ZoekRapporten
        }, {
            path: routePaths.zoekGoedePraktijken,
            component: ZoekGoedePraktijk
        }, {
            path: routePaths.zoekShmPrestaties,
            component: ZoekShmPrestaties
        }
    ]), [gemSource, apiUrls, refreshLists, routePaths])

    const [isLoading, setIsLoading] = useState<{ homeData: boolean, pageData: boolean }>({ homeData: true, pageData: false });
    const setPageLoading = useCallback((isLoading: boolean) => setIsLoading(prev => ({ ...prev, pageData: isLoading })), []);

    return <Layout>
        <ScrollToTop />
        <LoadPanel
            visible={isLoading.homeData || isLoading.pageData}
            showPane showIndicator
            shading shadingColor="rgba(0,0,0,0.2)"
            delay={0}
            message="Bezig met laden..."
        />
        <Switch>
            <Route exact path='/' component={Home} />

            {pages.map(({ component: Component, props, path, secure }, idx) => secure
                //if secure path
                ? <AuthorizationRoute exact path={path} key={idx}>
                    <Component {...props} apiUrls={apiUrls} lists={lists} refreshLists={refreshLists} setPageLoading={setPageLoading} />
                </AuthorizationRoute>
                //else public path
                : <Route exact path={path} key={idx} >
                    <Component {...props} apiUrls={apiUrls} lists={lists} setPageLoading={setPageLoading} />
                </Route>
            )}

            <Route path={ApplicationPaths.ApiAuthorizationPrefix} component={ApiAuthorizationRoutes} />
        </Switch>
 
    </Layout>     
}

export default App;
